/**
 * Substring highlighting for the typeahead + SearchView result
 * rows (P74.2).
 *
 * Returns the input text split into a sequence of (text, match)
 * segments. The renderer wraps `match: true` segments in <mark>
 * (or however it wants to highlight). Case-insensitive substring
 * match on the FULL query string -- NOT a re-derivation of the
 * backend's trigram ranking. The highlight is a visual aid that
 * answers "which letters in this row did I type?".
 */

export interface HighlightSegment {
  text: string;
  match: boolean;
}

const MIN_QUERY_LENGTH = 2;

export function highlightMatches(
  text: string | null | undefined,
  query: string | null | undefined,
): HighlightSegment[] {
  const safeText = text ?? "";
  const safeQuery = (query ?? "").trim();
  if (safeText.length === 0) {
    return [];
  }
  if (safeQuery.length < MIN_QUERY_LENGTH) {
    return [{ text: safeText, match: false }];
  }
  const haystack = safeText.toLowerCase();
  const needle = safeQuery.toLowerCase();
  const segments: HighlightSegment[] = [];
  let cursor = 0;
  while (cursor < safeText.length) {
    const hit = haystack.indexOf(needle, cursor);
    if (hit === -1) {
      segments.push({ text: safeText.slice(cursor), match: false });
      break;
    }
    if (hit > cursor) {
      segments.push({ text: safeText.slice(cursor, hit), match: false });
    }
    segments.push({
      text: safeText.slice(hit, hit + needle.length),
      match: true,
    });
    cursor = hit + needle.length;
  }
  return segments;
}
